/**
 * Utility per la conversione CAP → Coordinate GPS
 *
 * Utilizza un database locale di CAP italiani per convertire
 * i codici postali in coordinate geografiche per la visualizzazione su mappa.
 */

import capDatabase from "../data/cap-italiani.json";

/**
 * Converte un CAP italiano in coordinate GPS con supporto zona/località
 * @param {string|number} cap - Codice di Avviamento Postale (5 cifre)
 * @param {string|null} zona - Nome della zona/località (opzionale)
 * @returns {object|null} - { lat, lng, comune, provincia, matchType } oppure null
 */
export function capToCoordinates(cap, zona = null) {
  if (!cap) return null;

  // Normalizza il CAP (rimuovi spazi, assicura 5 cifre)
  const normalizedCap = String(cap).trim().padStart(5, "0");

  // 1° TENTATIVO: Ricerca con CAP + zona (match esatto)
  if (zona) {
    const normalizedZona = zona.toLowerCase().trim();
    const keyEsatto = `${normalizedCap}-${normalizedZona}`;

    if (capDatabase[keyEsatto]) {
      console.log(`✅ Match esatto: ${keyEsatto}`);
      return {
        lat: capDatabase[keyEsatto].lat,
        lng: capDatabase[keyEsatto].lng,
        comune: capDatabase[keyEsatto].comune,
        provincia: capDatabase[keyEsatto].provincia,
        matchType: "exact", // Match preciso CAP+zona
      };
    }
  }

  // 2° TENTATIVO: Ricerca solo CAP (coordinate medie)
  const coordinates = capDatabase[normalizedCap];

  if (coordinates) {
    console.log(`⚠️ Fallback CAP: ${normalizedCap}`);
    return {
      lat: coordinates.lat,
      lng: coordinates.lng,
      comune: coordinates.comune || null,
      provincia: coordinates.provincia || null,
      matchType: "cap", // Fallback su CAP
    };
  }

  // 3° TENTATIVO: Fallback su prefisso (area più ampia)
  const prefix = normalizedCap.substring(0, 3);
  const fallbackKey = Object.keys(capDatabase).find(
    (key) => key.startsWith(prefix) && !key.includes("-"),
  );

  if (fallbackKey) {
    console.warn(`⚠️ Fallback prefisso: ${normalizedCap} → ${fallbackKey}`);
    return {
      lat: capDatabase[fallbackKey].lat,
      lng: capDatabase[fallbackKey].lng,
      comune: capDatabase[fallbackKey].comune,
      provincia: capDatabase[fallbackKey].provincia,
      matchType: "prefix", // Fallback su prefisso
      approximate: true,
    };
  }

  console.error(`❌ CAP ${normalizedCap} non trovato nel database`);
  return null;
}

/**
 * Converte un array di operatori aggiungendo le coordinate GPS
 * @param {Array} operatori - Array di oggetti operatore con campi 'cap' e 'zona'
 * @returns {Array} - Operatori con coordinate aggiunte (esclude quelli senza coordinate valide)
 */
export function addCoordinatesToOperators(operatori) {
  if (!Array.isArray(operatori)) {
    console.error("addCoordinatesToOperators: parametro non è un array");
    return [];
  }

  const result = [];
  let exactMatches = 0;
  let capMatches = 0;
  let prefixMatches = 0;
  let failCount = 0;

  for (const operatore of operatori) {
    // Passa sia CAP che zona alla funzione
    const coords = capToCoordinates(operatore.cap, operatore.zona);

    if (coords) {
      result.push({
        ...operatore,
        gps: coords,
      });

      // Conteggio per tipo di match
      if (coords.matchType === "exact") exactMatches++;
      else if (coords.matchType === "cap") capMatches++;
      else if (coords.matchType === "prefix") prefixMatches++;
    } else {
      failCount++;
      console.warn(
        `Operatore ${operatore.id || "N/A"} senza coordinate valide (CAP: ${operatore.cap}, Zona: ${operatore.zona || "N/A"})`,
      );
    }
  }

  // Log statistiche dettagliate
  console.log(`
📊 Statistiche Geocoding:
  ✅ Match esatti (CAP+zona): ${exactMatches}
  ⚠️ Match CAP (media): ${capMatches}
  ⚠️ Match prefisso (approssimato): ${prefixMatches}
  ❌ Falliti: ${failCount}
  📍 Totale posizionati: ${result.length}/${operatori.length}
  `);

  return result;
}

/**
 * Verifica se un CAP esiste nel database
 * @param {string|number} cap - Codice di Avviamento Postale
 * @returns {boolean} - true se il CAP esiste
 */
export function isCapValid(cap) {
  if (!cap) return false;
  const normalizedCap = String(cap).trim().padStart(5, "0");
  return capDatabase.hasOwnProperty(normalizedCap);
}

/**
 * Ottiene le statistiche del database CAP
 * @returns {object} - Statistiche del database
 */
export function getCapDatabaseStats() {
  const caps = Object.keys(capDatabase);
  const province = [
    ...new Set(Object.values(capDatabase).map((c) => c.provincia)),
  ];

  return {
    totalCaps: caps.length,
    totalProvince: province.length,
    province: province.sort(),
  };
}

export default {
  capToCoordinates,
  addCoordinatesToOperators,
  isCapValid,
  getCapDatabaseStats,
};
